import type { FieldDefinition, Json, Money } from "@estelle/domain";
import type { ReactNode } from "react";

import { renderCell } from "@/components/table/cells";

/**
 * The order detail is not a `DataTable` row, but money and dates still have
 * to look exactly like they do in every list — same currency grouping, same
 * IST day boundary. Fabricating the minimal `FieldDefinition` `renderCell`
 * needs and calling straight into it means there is one formatter in the
 * app, not a second copy here that quietly drifts from the first.
 */
const MONEY_FIELD: FieldDefinition = {
  name: "amount",
  label: "Amount",
  type: "money",
};
const DATE_FIELD: FieldDefinition = {
  name: "date",
  label: "Date",
  type: "date",
};
const DATETIME_FIELD: FieldDefinition = {
  name: "datetime",
  label: "Date",
  type: "datetime",
};

export function money(value: Money | null): ReactNode {
  return renderCell(MONEY_FIELD, value);
}

export function dateOnly(value: string | null): ReactNode {
  return renderCell(DATE_FIELD, value);
}

export function dateTime(value: string | null): ReactNode {
  return renderCell(DATETIME_FIELD, value);
}

const IST = "Asia/Kolkata";
const DAY_MS = 86_400_000;

function istKey(date: Date): string {
  // `en-CA` is `YYYY-MM-DD` — the one locale format that sorts and diffs as
  // a string exactly as it would as a date, which is all this needs it for.
  return new Intl.DateTimeFormat("en-CA", { timeZone: IST }).format(date);
}

function istTime(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: IST,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
    .format(date)
    .toLowerCase();
}

/**
 * The orders list's placed-at column, at Shopify parity (#350): `Today at
 * 12:22 am`, `Yesterday at 9:54 pm`, a weekday name inside the last week,
 * then `Aug 24 at 7:24 am` — a year only where the order's is not the
 * current one. Always IST, the shop's day boundary rather than the
 * browser's, matching `formatDate` in `components/table/cells.tsx`.
 *
 * `now` is a parameter rather than always `new Date()` so a test can hold it
 * still; every caller in the app passes nothing and gets the real clock.
 */
export function relativeDateTime(
  value: string | null,
  now: Date = new Date(),
): ReactNode {
  if (value === null) return <span className="text-muted-foreground">—</span>;
  const placed = new Date(value);
  if (Number.isNaN(placed.getTime())) return value;

  const time = istTime(placed);
  const todayKey = istKey(now);
  const placedKey = istKey(placed);
  if (placedKey === todayKey) return `Today at ${time}`;

  const yesterdayKey = istKey(new Date(now.getTime() - DAY_MS));
  if (placedKey === yesterdayKey) return `Yesterday at ${time}`;

  const daysAgo = Math.round(
    (Date.parse(`${todayKey}T00:00:00Z`) -
      Date.parse(`${placedKey}T00:00:00Z`)) /
      DAY_MS,
  );
  if (daysAgo > 0 && daysAgo < 7) {
    const weekday = new Intl.DateTimeFormat("en-US", {
      timeZone: IST,
      weekday: "long",
    }).format(placed);
    return `${weekday} at ${time}`;
  }

  const sameYear = todayKey.slice(0, 4) === placedKey.slice(0, 4);
  const datePart = new Intl.DateTimeFormat("en-US", {
    timeZone: IST,
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(placed);
  return `${datePart} at ${time}`;
}

/** `1 item`, `2 items` — Shopify's wording for `itemCount` (#350). */
export function itemsLabel(count: number): string {
  return `${String(count)} item${count === 1 ? "" : "s"}`;
}

/**
 * The mobile card's compact clock time — `12:22 AM`, uppercase, with no
 * day information: the card sits under a day heading already saying which
 * day (#350).
 */
export function cardTime(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: IST,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(parsed);
}

/**
 * The mobile list's day heading: `Today`, `Yesterday`, then a calendar date
 * (#350) — the same three-step ladder as `relativeDateTime` minus the
 * weekday-name middle rung, which describes a single order's own time and
 * has no place on a heading shared by every order placed that day.
 */
export function dayGroupLabel(value: string, now: Date = new Date()): string {
  const placed = new Date(value);
  if (Number.isNaN(placed.getTime())) return value;

  const todayKey = istKey(now);
  const placedKey = istKey(placed);
  if (placedKey === todayKey) return "Today";

  const yesterdayKey = istKey(new Date(now.getTime() - DAY_MS));
  if (placedKey === yesterdayKey) return "Yesterday";

  const sameYear = todayKey.slice(0, 4) === placedKey.slice(0, 4);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: IST,
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(placed);
}

/**
 * A change-history `before`/`after` value. It is `Json`, not a display
 * type — a scalar prints as itself, but a plain `String()` on the object or
 * array shape a line-item or address change carries would render
 * `"[object Object]"` for every such row (`no-base-to-string` catches this
 * for a reason). `JSON.stringify` is the honest fallback: unreadable-ish for
 * a nested object, but at least it is the actual value.
 */
export function eventValue(value: Json | null): string {
  if (value === null) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}
